// ====================================
// MAIN API FUNCTION FOR PRODUCT PACKAGE SUBMISSION
// ====================================

import { apiSend, getRoute } from "@services/axios";
import { store } from "@services/redux/store";

/**
 * Main function to handle package product submissions by step level
 * @param {Object} productData - Product data for the current step
 * @param {number} level - Step level (1, 2, 3, 4)
 * @returns {Promise} API response
 */
export const postPackage = async (productId = null, productData, level) => {
  switch (level) {
    case 1:
      return await postPackageStep1(productData);

    case 2:
      return await postPackageStep2(productId);

    case 3:
      return await postPackageStep3(productId);

    case 4:
      return await postPackageStep4(productId);

    default:
      throw new Error(`Unsupported level: ${level} for package product`);
  }
};

export default postPackage;

// ====================================
// STEP 1 - CREATE PACKAGE PRODUCT
// ====================================

export const postPackageStep1 = async (productData) => {
  try {
    const url = `${getRoute("create_package_step_1")}`;
    const response = await apiSend(url, "post", productData);
    handleBackendErrors(response.data);
    localStorage.setItem("product_id_save", response?.data?.result?.product_id);
    return response.data.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 2 - UPDATE PACKAGE DETAILS
// ====================================

export const postPackageStep2 = async (productId) => {
  try {
    const productData = store.getState().package?.step2;
    const url = `${getRoute("create_package_step_2")}/${productId}`;
    const response = await apiSend(url, "post", productData);
    handleBackendErrors(response.data);
    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 3 - PRICING & POLICIES
// ====================================

export const postPackageStep3 = async (productId) => {
  try {
    const productData = store.getState().package?.step3;
    console.log(productData);
    const url = `${getRoute("create_package_step_3")}/${productId}`;
    const response = await apiSend(url, "post", productData);
    handleBackendErrors(response.data);
    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 4 - ADDITIONAL SERVICES / FLIGHTS / ACTIVITIES
// ====================================

export const postPackageStep4 = async (productId) => {
  try {
    const productData = store.getState().package?.step4;

    const url = `${getRoute("create_package_step_4")}/${productId}`;
    const response = await apiSend(url, "post", productData);
    handleBackendErrors(response.data);
    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// HELPER FUNCTIONS
// ====================================

function handleBackendErrors(data) {
  if (!data) return;

  const backendMsg = data?.request?.message;
  const backendError = data?.result?.Error;

  if (backendMsg && backendMsg.toLowerCase().includes("invalid request data")) {
    if (backendError && typeof backendError === "object") {
      console.table(flattenBackendErrors(backendError));
    }
    throw new Error(backendMsg);
  }

  if (backendError && typeof backendError === "object") {
    const firstKey = Object.keys(backendError)[0];
    const firstMsg = backendError[firstKey]?.[0] || "Unknown backend error";
    throw new Error(firstMsg);
  }
}

function formatBackendError(error) {
  if (error?.response?.data) {
    handleBackendErrors(error.response.data);
  }
  return new Error(
    error.message || "An unknown error occurred during package submission."
  );
}

function flattenBackendErrors(errorObj) {
  const rows = [];
  for (const key in errorObj) {
    const value = errorObj[key];
    if (Array.isArray(value)) {
      value.forEach((msg) => rows.push({ field: key, message: msg }));
    } else if (typeof value === "object") {
      for (const subKey in value) {
        rows.push({ field: `${key}.${subKey}`, message: value[subKey] });
      }
    } else {
      rows.push({ field: key, message: value });
    }
  }
  return rows;
}
